const MAX_WIDTH = 1200
const MAX_HEIGHT = 1200
const QUALITY = 0.8

/**
 * Compress and orient an image file.
 * Uses createImageBitmap with imageOrientation to handle EXIF rotation.
 * Returns a JPEG blob ready for upload.
 */
export async function compressImage(file: File): Promise<Blob> {
  // createImageBitmap handles EXIF orientation in modern browsers
  const bitmap = await createImageBitmap(file, {
    imageOrientation: 'from-image',
  })

  // Calculate scaled dimensions
  let { width, height } = bitmap
  if (width > MAX_WIDTH || height > MAX_HEIGHT) {
    const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height)
    width = Math.round(width * ratio)
    height = Math.round(height * ratio)
  }

  // Draw to canvas (strips EXIF, applies orientation)
  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()

  // Export as JPEG
  const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY })
  return blob
}

/**
 * Upload a photo to R2 via pre-signed URL.
 * 1. Compress the image client-side
 * 2. Get a pre-signed URL from the API
 * 3. PUT directly to R2
 * 4. Confirm the upload with the API
 * Returns the updated photo_urls array.
 */
export async function uploadPhoto(
  itemId: string,
  file: File,
  onProgress?: (stage: 'compressing' | 'uploading' | 'confirming') => void
): Promise<string[]> {
  // 1. Compress
  onProgress?.('compressing')
  const compressed = await compressImage(file)

  // 2. Get pre-signed URL
  onProgress?.('uploading')
  const presignRes = await fetch('/api/photos/presign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ item_id: itemId }),
  })

  if (!presignRes.ok) {
    const err = await presignRes.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? 'Failed to get upload URL')
  }

  const { upload_url, key } = await presignRes.json() as { upload_url: string; key: string }

  // 3. PUT directly to R2
  const uploadRes = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg' },
    body: compressed,
  })

  if (!uploadRes.ok) {
    throw new Error(`Upload failed: ${uploadRes.status}`)
  }

  // 4. Confirm with API
  onProgress?.('confirming')
  const confirmRes = await fetch('/api/photos/confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ item_id: itemId, key }),
  })

  if (!confirmRes.ok) {
    const err = await confirmRes.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? 'Failed to confirm upload')
  }

  const { photo_urls } = await confirmRes.json() as { photo_urls: string[] }
  return photo_urls
}
